import React, { useState, useEffect, useLayoutEffect } from 'react'
import PropTypes from 'prop-types'
import { useMakeSelector } from 'hooks/redux'
import { makeComponentMetaSelector } from 'selectors/navigator'
import { prop } from 'ramda'
import { QUERY_BOX_TYPES } from 'utils/constants'
import { findRefOffsetToParent } from 'utils/pagePosition'
import {
  LINE_SPACING,
  LINE_RATIO,
  PUSH_LINE_DOWN,
  PUSH_LINE_UP,
  LINE_GAP,
  CURVE_WIDTH,
  GAP_WIDTH,
} from 'constants/nodeConnectors'

import { StyledConnectingGraph, StyledCircle } from './NodeConnector.style'

const BLIP_GAP = 66
const ANIMATION_DURATION = 8

const NodeConnector = ({
  carryBoxPosition,
  rungNodeUri,
  carryNodeUri,
  isSimulating,
  nodeCarryEvaluation,
  simulationPlaying,
  showAddOption,
}) => {
  const uniquePathId =
    `path-between-${rungNodeUri}-and-${carryNodeUri}`.replaceAll('/', '-')
  // useRef was not creating refs when I expected it to in the render cycle
  // using useState to create a ref accomplished what I needed to
  const [parentRef, setParentRef] = useState(null)
  const [pathRef, setPathRef] = useState(null)
  const [blips, setBlips] = useState(null)

  const rungItem = useMakeSelector(makeComponentMetaSelector, {
    id: rungNodeUri,
  })
  const carryItem = useMakeSelector(makeComponentMetaSelector, {
    id: carryNodeUri,
  })
  const fnItem = useMakeSelector(makeComponentMetaSelector, {
    id: prop('fnId', rungItem),
  })

  useLayoutEffect(() => {
    // determine how many blips to show on the evaluation carry
    // only runs once the path has a ref
    // only executes once, when there are no blips
    if (pathRef !== null && blips === null) {
      const length = pathRef.getTotalLength()
      const numBlips = Math.ceil(length / BLIP_GAP)
      setBlips(Array(numBlips).fill(numBlips))
    }
  }, [pathRef, blips])

  useEffect(() => {
    if (parentRef) {
      if (simulationPlaying) {
        parentRef.unpauseAnimations()
      } else {
        parentRef.pauseAnimations()
      }
    }
  }, [parentRef, simulationPlaying])

  const outOfScope = prop('fnId', rungItem) !== prop('fnId', carryItem)

  if (!rungItem.getRef || !carryItem.getRef || outOfScope || !fnItem.getRef) {
    return null
  }

  const rungRef = rungItem.getRef()
  const carryRef = carryItem.getRef()
  const fnRef = fnItem.getRef()

  // there is a moment when changing status that these come back as null
  // I believe due to showing loading, rather than the graphs
  // should probably have a different kind of 'something is happening' indicator
  if (!rungRef.current || !carryRef.current) return null

  const { offsetHeight: rungHeight } = rungRef.current
  const { offsetHeight: carryHeight } = carryRef.current

  if (showAddOption && rungHeight) {
    const svgHeight = rungHeight
    return (
      <StyledConnectingGraph
        width={LINE_GAP}
        height={svgHeight}
        viewBox={`0 0 ${LINE_GAP} ${svgHeight}`}
        data-testid="SVGElm"
      >
        <g fillRule="nonzero">
          <path d={`M ${LINE_GAP}, ${svgHeight / 2} H 100`} />
        </g>
      </StyledConnectingGraph>
    )
  }

  const pushToConditionExit = QUERY_BOX_TYPES.has(carryItem.type)

  const rungExitHeight =
    findRefOffsetToParent(rungRef, fnRef, 'offsetTop') + rungHeight / 2
  const carryExitHeight =
    findRefOffsetToParent(carryRef, fnRef, 'offsetTop') + carryHeight / 2

  const verticalHeight = carryExitHeight - rungExitHeight
  const carryAboveRung = verticalHeight < 0

  const lineFraction = (LINE_GAP - CURVE_WIDTH * 2) / LINE_RATIO

  const upAdjustment = pushToConditionExit && carryAboveRung ? PUSH_LINE_UP : 0
  const adjustment =
    pushToConditionExit && !carryAboveRung ? PUSH_LINE_DOWN : upAdjustment
  const height = Math.abs(verticalHeight) + adjustment

  const widthRung =
    lineFraction * (LINE_RATIO - 1) - carryBoxPosition * LINE_SPACING - 10
  const widthCarry = lineFraction + carryBoxPosition * LINE_SPACING + 10

  const origin = carryAboveRung ? `0,0` : `0,${height - 2}`
  const verticalLineHeight = height - 3 * CURVE_WIDTH

  const directionChange = carryAboveRung ? CURVE_WIDTH : CURVE_WIDTH * -1
  const boxHeight = height > 0 ? height : 1

  const pathRules =
    height > CURVE_WIDTH
      ? `
      M${origin}
      h ${widthCarry}
      q ${CURVE_WIDTH},0 ${CURVE_WIDTH},${directionChange}
      v ${carryAboveRung ? verticalLineHeight : verticalLineHeight * -1}
      q 0,${directionChange} ${CURVE_WIDTH},${directionChange}
      h ${widthRung}
      `
      : `
      M${origin}
      h${GAP_WIDTH}
    `

  return (
    <StyledConnectingGraph
      width={LINE_GAP}
      height={boxHeight}
      viewBox={`0 0 ${LINE_GAP} ${boxHeight}`}
      topValue={rungHeight / 2}
      carryAboveRung={carryAboveRung}
      isSimulating={isSimulating}
      nodeCarryEvaluation={nodeCarryEvaluation}
      ref={setParentRef}
    >
      <g fillRule="nonzero">
        <path d={pathRules} id={uniquePathId} ref={setPathRef} />
        {isSimulating &&
          nodeCarryEvaluation &&
          blips.map((_, idx, arr) => (
            <StyledCircle
              r="4"
              key={`circle-${idx}`} // eslint-disable-line
            >
              <animateMotion
                dur={`${ANIMATION_DURATION}s`}
                // animations are over a ANIMATION_DURATION second period
                // this sets the delay for each blip in an equal
                // increment so they are evenly spaced along
                // the path they travel. this _should_ give
                // the perception that all blips on the screen
                // are travelling at the same speed
                begin={`${(idx * ANIMATION_DURATION) / arr.length}s`}
                repeatCount="indefinite"
              >
                <mpath xlinkHref={`#${uniquePathId}`} />
              </animateMotion>
            </StyledCircle>
          ))}
      </g>
    </StyledConnectingGraph>
  )
}

NodeConnector.propTypes = {
  className: PropTypes.string,
  carryNodeUri: PropTypes.string.isRequired,
  rungNodeUri: PropTypes.string.isRequired,
  carryBoxPosition: PropTypes.number,
  carryBoxColumn: PropTypes.number,
  isSimulating: PropTypes.bool,
  nodeCarryEvaluation: PropTypes.bool,
  simulationPlaying: PropTypes.bool,
  showAddOption: PropTypes.bool,
}
export default NodeConnector
